import { Injectable } from '@angular/core'
import { forkJoin, Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { ApiService } from 'src/app/services/api.service'
import {
  ApplicationDto,
  ApplicationType,
  AppUsageDto,
  AppUsageReportDto,
  DailyUsageRecordDto,
  EntitlementDto,
  LastBackupReportDto,
  MonthlyUsageRecordDto,
  MonthlyUsageReportDto,
  ResourceTypeUsageDto,
  UsageReportDto,
} from 'src/app/services/dtos'
import {
  BillingCycleVm,
  Cloud,
  DailyAppVm,
  DailyBreakdownRowVm,
  DailyPointVm,
  KpiVm,
  MonthlyPointVm,
  UsageDashboardVm,
} from './usage-billing.model'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// Widest trend window the UI offers, and also the server-side cap — the monthly
// series is scanned from daily HWM snapshots, whose retention only covers ~13 months.
const TREND_MONTHS = 12

// First billing cycle Arpio holds a complete set of daily HWM snapshots for. Recording
// started part-way through Feb 2026, so that cycle understates its high water mark and
// every earlier one reports a zero the account never actually had. The trend series is
// floored here rather than plotting fabricated history; the period filter still reaches
// back over the whole trend window, and the UI names the gap when a selection lands in it.
//
// TODO: account-agnostic, so an account onboarded after this date is told the product has
// no data before Mar 2026 rather than being told its own start. Retire it once the usage
// endpoints report the first cycle they hold snapshots for, and keep this only as the
// fallback for a response that omits it.
export const USAGE_DATA_START_MONTH = '2026-03'
/** Same floor as a full date, for comparing against a selected period. */
export const USAGE_DATA_START_DATE = `${USAGE_DATA_START_MONTH}-01`

// apps.json has no per-app cloud, so we resolve it by joining the app id to the
// account's applications (each carries an authoritative AWS/AZURE type). Apps missing
// from that join (e.g. deleted apps) fall back to inspecting resource types: Azure
// types are ARM provider paths ('Microsoft.Compute/virtualMachines'), AWS types are
// bare camelCase keys ('ec2Instance'), so the namespace prefix is the discriminator.
// The backend classifies the same field by exact registry lookup rather than prefix;
// every registered Azure type uses this namespace, so the two agree in practice.
const AZURE_TYPE_PREFIX = 'microsoft.'

/**
 * Fetches the real Usage & Billing data (ARP-7661) and maps it to the dashboard's
 * UsageDashboardVm. Two slices degrade rather than fail while their backend PRs are
 * unmerged, so the dashboard renders either way:
 *   - monthly[] trend (ARP-7925) → optional request; an error yields an empty series
 *     and the component's canViewMonthly gate disables the Monthly toggle.
 *   - cycle bounds (ARP-7926) → fall back to the daily records' first/last date,
 *     which understates an in-progress cycle's end.
 * per-app cloud is not on apps.json at all; it's joined from the applications list.
 */
@Injectable({ providedIn: 'root' })
export class UsageBillingService {
  constructor(private apiService: ApiService) {}

  /** Load the dashboard for a billing month (YYYY-MM). Errors on the required
   *  requests (incl. 403 for un-entitled accounts) propagate to the caller. */
  loadDashboard(accountId: string, month: string): Observable<UsageDashboardVm> {
    return forkJoin({
      usage: this.apiService.getUsageReport(accountId, month),
      apps: this.apiService.getAppUsageReport(accountId, month),
      entitlement: this.apiService.getEntitlement(accountId),
      applications: this.apiService.listApplications(accountId),
      // Optional: the trend chart is one tab of the dashboard, so a missing or
      // failing monthly endpoint must not take the whole page down with it.
      monthly: this.apiService.getMonthlyUsageReport(accountId, TREND_MONTHS).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ usage, apps, entitlement, applications, monthly }) =>
        this.toDashboardVm(usage, apps, entitlement, applications, monthly)
      )
    )
  }

  /**
   * Daily points for one billing cycle, named by the month that cycle starts in.
   *
   * The dashboard loads the current cycle up front; a period reaching further back has
   * to pull its neighbours, because this endpoint serves one cycle per request. Only
   * the series is mapped — the KPIs and the per-application breakdown stay pinned to
   * the current cycle, which is what they describe.
   */
  loadDailyForCycle(accountId: string, month: string): Observable<DailyPointVm[]> {
    return this.apiService.getUsageReport(accountId, month).pipe(
      map((usage) => {
        const contracted = normalizeBaseline(usage.contractBaseline)
        return byDate(usage.dailyRecords).map((r) => this.toDailyPoint(r, contracted))
      })
    )
  }

  /**
   * Per-application breakdown for the given cycles.
   *
   * Like the daily series, apps.json serves one cycle per request, so a period outside
   * the cycle loaded at startup has to fetch its own. The applications list is pulled
   * once and shared across them — it only supplies the authoritative per-app cloud,
   * which doesn't vary by cycle.
   */
  loadAppBreakdownForCycles(accountId: string, months: string[]): Observable<DailyBreakdownRowVm[]> {
    if (!months.length) return of([])
    return forkJoin({
      applications: this.apiService.listApplications(accountId).pipe(catchError(() => of([] as ApplicationDto[]))),
      // A cycle outside retention 404s; that's a gap in the breakdown, not a failure.
      reports: forkJoin(
        months.map((month) => this.apiService.getAppUsageReport(accountId, month).pipe(catchError(() => of(null))))
      ),
    }).pipe(
      map(({ applications, reports }) => {
        const cloudByApp = this.cloudByApp(applications)
        return reports
          .filter((report): report is AppUsageReportDto => report != null)
          .flatMap((report) =>
            byDate(report.dailyRecords).map((r) =>
              this.toBreakdownRow(r.date, r.accountHwm, r.byApp, normalizeBaseline(report.contractBaseline), cloudByApp)
            )
          )
      })
    )
  }

  /** Per-application counts as of the last backup, as one row dated by the newest backup used. */
  loadLastBackupBreakdown(accountId: string): Observable<DailyBreakdownRowVm | null> {
    return forkJoin({
      applications: this.apiService.listApplications(accountId).pipe(catchError(() => of([] as ApplicationDto[]))),
      report: this.apiService.getLastBackupReport(accountId),
    }).pipe(
      map(({ applications, report }: { applications: ApplicationDto[]; report: LastBackupReportDto }) =>
        report.asOf
          ? this.toBreakdownRow(
              report.asOf.slice(0, 10),
              report.billableCount,
              report.byApp,
              normalizeBaseline(report.contractBaseline),
              this.cloudByApp(applications)
            )
          : null
      )
    )
  }

  /** Authoritative per-app cloud, keyed by app id (falls back to type inference). */
  private cloudByApp(applications: ApplicationDto[]): Map<string, Cloud> {
    return new Map<string, Cloud>(
      applications
        .filter((a) => a.appId)
        .map((a) => [a.appId as string, a.type === ApplicationType.AZURE ? 'AZURE' : 'AWS'])
    )
  }

  private toDashboardVm(
    usage: UsageReportDto,
    apps: AppUsageReportDto,
    entitlement: EntitlementDto,
    applications: ApplicationDto[],
    monthly: MonthlyUsageReportDto | null
  ): UsageDashboardVm {
    const cloudByApp = this.cloudByApp(applications)
    const contracted = normalizeBaseline(usage.contractBaseline ?? entitlement.licensedBillableResourceCount)
    // Sort once here: the cycle bounds, the "current count" KPI and the chart all read
    // this series positionally, so date order is a precondition rather than a nicety.
    const dailyRecords = byDate(usage.dailyRecords)
    const daily = dailyRecords.map((r) => this.toDailyPoint(r, contracted))
    const dailyBreakdown = byDate(apps.dailyRecords).map((r) =>
      this.toBreakdownRow(r.date, r.accountHwm, r.byApp, contracted, cloudByApp)
    )
    // Sorted for the same reason the daily records are: the trend chart plots this series
    // on an index axis, so a flipped feed would draw the months backwards, and the window
    // start is read off the first element.
    const monthlyRecords = byMonthKey(monthly?.records ?? [])

    return {
      billingCycle: this.toBillingCycle(usage, dailyRecords),
      kpis: this.toKpis(dailyRecords, applications, usage.monthlyHwm, contracted),
      trendWindowStart: monthlyRecords[0]?.monthKey ?? '',
      monthly: this.toMonthly(monthlyRecords),
      daily,
      dailyBreakdown,
    }
  }

  /** Monthly HWM trend, oldest cycle first, floored at the first cycle with complete
   *  records. No records (endpoint unavailable) yields an empty series, which the
   *  component reads as "monthly view unavailable". */
  private toMonthly(records: MonthlyUsageRecordDto[]): MonthlyPointVm[] {
    return records.filter(hasUsageRecords).map((r) => {
      // Per-cloud counts are the peak day's split, so they sum to monthlyHwm. A cycle
      // with no snapshots reports an empty byCloud, which correctly reads as 0.
      const aws = r.byCloud.find((c) => c.cloud === 'AWS')
      const azure = r.byCloud.find((c) => c.cloud === 'AZURE')
      return {
        monthKey: r.monthKey,
        label: r.label,
        hwm: r.monthlyHwm,
        awsHwm: aws?.billableCount ?? 0,
        azureHwm: azure?.billableCount ?? 0,
        contracted: r.contractBaseline ?? null,
      }
    })
  }

  private toDailyPoint(r: DailyUsageRecordDto, contracted: number | null): DailyPointVm {
    const billable = sumBillable(r.byType)
    const total = sumTotal(r.byType)
    const aws = r.byCloud.find((c) => c.cloud === 'AWS')
    const azure = r.byCloud.find((c) => c.cloud === 'AZURE')
    return {
      date: r.date,
      dayLabel: dayLabel(r.date),
      billable,
      total,
      awsBillable: aws?.billableCount ?? 0,
      azureBillable: azure?.billableCount ?? 0,
      awsTotal: aws?.resourceCount ?? 0,
      azureTotal: azure?.resourceCount ?? 0,
      contracted,
      over: contracted != null && billable > contracted,
    }
  }

  private toBreakdownRow(
    date: string,
    accountHwm: number,
    byApp: AppUsageDto[],
    contracted: number | null,
    cloudByApp: Map<string, Cloud>
  ): DailyBreakdownRowVm {
    const topApps: DailyAppVm[] = byApp
      .map((a) => ({
        appId: a.appId,
        appName: a.appName ?? a.appId,
        cloud: resolveCloud(cloudByApp, a),
        billableCount: a.billableCount,
        byType: a.byType.map((t) => ({ resourceType: t.resourceType, count: t.billableCount })),
      }))
      .sort((x, y) => y.billableCount - x.billableCount)
    return {
      date,
      dayLabel: dayLabel(date),
      billable: accountHwm,
      delta: contracted == null ? 0 : accountHwm - contracted,
      topApps,
    }
  }

  private toKpis(
    dailyRecords: DailyUsageRecordDto[],
    applications: ApplicationDto[],
    monthlyHwm: number,
    contracted: number | null
  ): KpiVm {
    // A zero high water mark has no peak day — every record ties at 0 and the reduce
    // would name the first one, reading as though usage genuinely peaked that day.
    const peak =
      monthlyHwm > 0
        ? dailyRecords.reduce<DailyUsageRecordDto | null>(
            (max, r) => (max == null || r.accountHwm > max.accountHwm ? r : max),
            null
          )
        : null
    const lastDaily = dailyRecords[dailyRecords.length - 1]
    // Counted from the account's application list, not from the day's usage snapshot.
    // The snapshot only lists apps with billable resources, so an account whose apps
    // aren't backing up would report zero applications while plainly having some.
    const appsAzure = applications.filter((a) => a.type === ApplicationType.AZURE).length
    return {
      monthlyHwm,
      monthlyHwmDateLabel: peak ? dayLabel(peak.date) : null,
      contracted,
      within: contracted == null || monthlyHwm <= contracted,
      delta: contracted == null ? 0 : monthlyHwm - contracted,
      // Current count + its "as of" all come from the latest daily HWM record, so they're
      // self-consistent and need no separate backend timestamp. A cycle with no records
      // reports nothing rather than borrowing the entitlement's live count, which would
      // pair a real number with a zero total and an "as of —".
      currentBillable: lastDaily ? sumBillable(lastDaily.byType) : null,
      currentAsOfLabel: lastDaily ? dayLabel(lastDaily.date) : '—',
      appsAws: applications.length - appsAzure,
      appsAzure,
      appsTotal: applications.length,
    }
  }

  private toBillingCycle(usage: UsageReportDto, records: DailyUsageRecordDto[]): BillingCycleVm {
    // Prefer the server's cycle bounds; they're the only correct source for an
    // in-progress cycle (whose daily records stop at today) and for Azure's
    // anniversary-dated cycles. Fall back to the daily record range when absent.
    const exactBounds = usage.cycleStart != null && usage.cycleEnd != null
    const start = usage.cycleStart ?? records[0]?.date
    const end = usage.cycleEnd ?? records[records.length - 1]?.date
    return {
      monthKey: usage.billingMonth,
      label: start && end ? cycleLabel(start, end) : usage.billingMonth,
      start: start ?? '',
      end: end ?? '',
      exactBounds,
      // Falling back on the requested month is exact for calendar-month billers; for an
      // Azure account sitting before its anchor day it reads the cycle starting this
      // month as current, one day-range early.
      isCurrent: usage.isCurrent ?? usage.billingMonth === todayMonthKey(),
    }
  }
}

// 'Jun 1 – Jun 30, 2026'. Azure's anniversary cycles can straddle a year boundary,
// so both years are shown when they differ.
function cycleLabel(start: string, end: string): string {
  const [startYear, endYear] = [start.slice(0, 4), end.slice(0, 4)]
  const from = startYear === endYear ? dayLabel(start) : `${dayLabel(start)}, ${startYear}`
  return `${from} – ${dayLabel(end)}, ${endYear}`
}

// Cycles predating the first snapshot come back as real records holding a zero, so they
// have to be dropped by month rather than by value.
function hasUsageRecords(record: MonthlyUsageRecordDto): boolean {
  return record.monthKey >= USAGE_DATA_START_MONTH
}

// A trial entitlement carries billable_resource_count = -1 as its "no contract" sentinel
// and the backend passes it through unchanged, so a negative baseline has to read as
// absent. Left as a number it renders a "0 / -1 Over" ratio and marks every point over
// contract.
function normalizeBaseline(baseline: number | null | undefined): number | null {
  return baseline == null || baseline < 0 ? null : baseline
}

// 'YYYY-MM' sorts lexicographically too, so cycle order is the same string compare.
function byMonthKey<T extends { monthKey: string }>(records: T[]): T[] {
  return records.slice().sort((a, b) => (a.monthKey < b.monthKey ? -1 : a.monthKey > b.monthKey ? 1 : 0))
}

// ISO dates sort lexicographically, so a plain string compare is the date order.
function byDate<T extends { date: string }>(records: T[]): T[] {
  return records.slice().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
}

function sumBillable(byType: ResourceTypeUsageDto[]): number {
  return byType.reduce((s, t) => s + t.billableCount, 0)
}
function sumTotal(byType: ResourceTypeUsageDto[]): number {
  return byType.reduce((s, t) => s + t.resourceCount, 0)
}
// Authoritative cloud from the applications join; falls back to resource-type
// inference for apps not in the list (e.g. deleted).
function resolveCloud(cloudByApp: Map<string, Cloud>, app: AppUsageDto): Cloud {
  return cloudByApp.get(app.appId) ?? inferCloud(app.byType)
}
function inferCloud(byType: ResourceTypeUsageDto[]): Cloud {
  return byType.some((t) => t.resourceType.toLowerCase().startsWith(AZURE_TYPE_PREFIX)) ? 'AZURE' : 'AWS'
}
function dayLabel(iso: string): string {
  const [, m, d] = iso.split('-').map(Number)
  return `${MONTHS[m - 1]} ${d}`
}
// UTC, matching the server's billing-cycle anchor.
function todayMonthKey(): string {
  return new Date().toISOString().slice(0, 7)
}
